/**
 * [화면 편집] 오른쪽 칸 — 쉬운 판(2026-10-30). 고른 것에 맞는 것만 보인다.
 *
 * - 글: [글 고치기] · 글자 크기(−·+) · 굵게 · 정렬 · 글자 색 · 글꼴
 * - 그림: [그림 바꾸기]
 * - 그림 영역: [그림 고르기]·[그림 빼기] · 가득 채우기·전체 보이기
 * - 입력 칸·선택 상자·체크 상자: 값(그 칸과 같은 종류로) · 안내 글 · 선택 상자의 항목, 글자 크기·색
 * - D-day: 기준일(직접 고르기 · 화면의 날짜 칸에 연결) · 남은 날·지난 날 · 앞뒤 글, 글자 크기·색
 * - 모두: 바탕 색 · 모서리 둥글게 · 투명도 · 가운데로 · 앞으로·뒤로 · [숫자로 맞추기](접혀 있다) · 원래대로·복제·지우기
 * - 아무것도 고르지 않았으면: 쓰는 법 세 줄 · 화면 크기 · 배경 색 · [처음 모습으로]
 *
 * 값은 고른 요소의 계산된 스타일에서 읽고(여럿이면 마지막에 고른 것), 고치면 고른 것 모두에 인라인 스타일로 적는다.
 */
import { useState, type ReactNode } from 'react';
import {
  AlignCenter,
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignLeft,
  AlignRight,
  Bold,
  BringToFront,
  ChevronDown,
  Clapperboard,
  Copy,
  ImageMinus,
  ImageUp,
  MousePointerClick,
  Move,
  Pencil,
  RotateCcw,
  SendToBack,
  SquareDashedMousePointer,
  Trash2,
  Undo2,
} from 'lucide-react';
import { layerName, parseCssColor, stepFontSize, unionBox, type AlignMode, type Box } from './model.js';
import { boxOf, isDday, isImageFrame, isTextLayer, isVideo, layerChildren } from './dom.js';
import {
  ddayDate,
  ddayText,
  fieldKind,
  fieldLabel,
  fieldValue,
  frameFit,
  frameImage,
  linkableDateFields,
  readDday,
  videoFit,
  videoSound,
  type DdayMode,
  type DdaySettings,
  type ImageFit,
} from './components.js';
import {
  Choice,
  ColorChoices,
  LinesField,
  NumberField,
  PanelButton,
  Section,
  SelectBox,
  Slider,
  Stepper,
  Toggle,
  ValueField,
} from './fields.js';

export interface DesignSize {
  width: number;
  height: number;
}

export interface EditActions {
  style: (changes: Record<string, string | null>, final: boolean) => void;
  box: (next: Partial<Box>, final: boolean) => void;
  align: (mode: AlignMode) => void;
  reorder: (direction: 1 | -1) => void;
  textEdit: () => void;
  textDone: () => void;
  replaceImage: () => void;
  /** 영상 — 다른 영상으로 바꾸기 · 맞춤 · 소리. */
  replaceVideo: () => void;
  videoFit: (fit: ImageFit) => void;
  videoSound: (on: boolean) => void;
  /** 그림 영역 — 파일을 골라 넣기 · 맞춤 · 빼기. */
  frameImage: () => void;
  frameFit: (fit: ImageFit) => void;
  frameClear: () => void;
  /** 입력 칸의 값(체크면 'true'·'false') · 선택 상자의 항목 · 안내 글. */
  fieldValue: (value: string) => void;
  /** D-day — 설정 · 화면의 날짜 칸에 연결(null 이면 직접 고른 날로). */
  dday: (patch: Partial<DdaySettings>) => void;
  ddayLink: (field: HTMLElement | null) => void;
  selectOptions: (lines: string[]) => void;
  placeholder: (text: string) => void;
  selectParent: () => void;
  duplicate: () => void;
  remove: () => void;
  reset: () => void;
  resetAll: () => void;
  design: (size: DesignSize) => void;
  pageBackground: (css: string | null, final: boolean) => void;
}

interface Props {
  elements: readonly HTMLElement[];
  /** 문서의 창 — 창을 props 로 넘기지 않는다. React 개발판이 바뀐 props 를 깊이 훑다가 창의 `parent`·`frames` 를 따라
   * 다른 샌드박스 iframe 에 닿으면 SecurityError 로 멈춘다. */
  getView: () => Window | null;
  body: HTMLElement | null;
  design: DesignSize;
  sizeChoices?: ReadonlyArray<{ label: string } & DesignSize>;
  editingText: boolean;
  canReset: boolean;
  canResetAll: boolean;
  canSelectParent: boolean;
  /** 문서가 실은 글꼴(@font-face) 이름 — 글꼴 고르기에 더한다. */
  fonts: readonly string[];
  /** 이 화면이 쓰는 색 — 색 고르기 맨 앞. */
  docColors: readonly string[];
  actions: EditActions;
}

const px = (value: string): number | null => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const FIELD_NAMES = {
  text: '입력 칸',
  number: '숫자 입력',
  date: '날짜 입력',
  time: '시각 입력',
  check: '체크 상자',
  select: '선택 상자',
  textarea: '여러 줄 입력',
  color: '색 입력',
  range: '밀대',
} as const;

const GENERIC_FONTS = [
  { value: 'sans-serif', label: '고딕 (기기 글꼴)' },
  { value: 'serif', label: '명조 (기기 글꼴)' },
];

/** 바탕을 바꿀 때 — 그라데이션·그림 바탕도 덮게 줄임 속성으로 적고, 따로 적힌 색·그림은 지운다. */
const background = (css: string | null): Record<string, string | null> => ({
  background: css,
  'background-color': null,
  'background-image': null,
});

export function EditPanel(props: Props): ReactNode {
  const view = props.getView();
  if (!view) return null;
  if (props.elements.length === 0) return <PagePanel {...props} />;

  const { elements, editingText, canReset, canSelectParent, fonts, docColors, actions } = props;
  const primary = elements[elements.length - 1]!;
  const style = view.getComputedStyle(primary);
  const multiple = elements.length > 1;
  const text = elements.every((element) => isTextLayer(element, view));
  const image = !multiple && primary.tagName.toLowerCase() === 'img';
  const frame = !multiple && isImageFrame(primary);
  const video = !multiple && isVideo(primary);
  const dday = !multiple && isDday(primary);
  const field = multiple ? null : fieldKind(primary);
  const kind = multiple
    ? `${elements.length}개 골랐습니다`
    : frame
      ? '그림 영역'
      : video
        ? '영상'
        : dday
        ? 'D-day'
        : field
          ? FIELD_NAMES[field]
          : text
            ? '글'
            : image
              ? '그림'
              : '상자';
  const name = multiple
    ? null
    : field
      ? fieldLabel(primary) || null
      : dday
        ? null
        : layerName({
            tag: primary.tagName,
            text: primary.textContent ?? '',
            childElements: layerChildren(primary).length,
            className: '',
            name: primary.getAttribute('data-name'),
          });
  const fill = parseCssColor(style.backgroundColor);
  const box = multiple ? unionBox(elements.map(boxOf))! : boxOf(primary);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="border-b border-slate-100 px-4 py-3.5">
        <div className="text-[15px] font-semibold text-slate-900">{kind}</div>
        {name && name !== kind ? <div className="mt-0.5 truncate text-[12.5px] text-slate-500">{name}</div> : null}
        {canSelectParent ? (
          <button
            type="button"
            className="mt-2 inline-flex items-center gap-1 text-[12.5px] font-medium text-blue-600 hover:underline"
            onClick={actions.selectParent}
          >
            <SquareDashedMousePointer className="h-3.5 w-3.5" />
            감싼 상자 고르기
          </button>
        ) : null}
      </div>

      {text ? (
        <Section title="글자">
          <div className="flex">
            <PanelButton
              tone={editingText ? 'primary' : 'plain'}
              icon={<Pencil className="h-4 w-4" />}
              label={editingText ? '다 고쳤습니다' : '글 고치기'}
              onClick={editingText ? actions.textDone : actions.textEdit}
            />
          </div>
          <TextControls
            element={primary}
            getView={props.getView}
            fonts={fonts}
            docColors={docColors}
            actions={actions}
          />
        </Section>
      ) : null}

      {frame ? <FrameSection frame={primary} actions={actions} /> : null}

      {video ? <VideoSection video={primary} actions={actions} /> : null}

      {field ? <FieldSection element={primary} kind={field} actions={actions} /> : null}

      {dday ? <DdaySection element={primary} actions={actions} /> : null}

      {field || dday ? (
        <Section title="글자">
          <TextControls
            element={primary}
            getView={props.getView}
            fonts={fonts}
            docColors={docColors}
            actions={actions}
          />
        </Section>
      ) : null}

      {image ? (
        <Section title="바꾸기">
          <div className="flex">
            <PanelButton
              icon={<ImageUp className="h-4 w-4" />}
              label="다른 그림으로 바꾸기"
              onClick={actions.replaceImage}
            />
          </div>
        </Section>
      ) : null}

      {image || video ? null : (
        <Section title="바탕 색">
          <ColorChoices
            label="바탕 색"
            current={fill && fill.a > 0 ? fill : null}
            docColors={docColors}
            none="바탕 없음"
            onPick={(css, final) => actions.style(background(css ?? 'transparent'), final)}
          />
        </Section>
      )}

      <Section title="모양">
        <Slider
          label="모서리 둥글게"
          value={Math.round(px(style.borderTopLeftRadius) ?? 0)}
          min={0}
          max={Math.max(40, Math.round(Math.min(box.width, box.height) / 2))}
          suffix="px"
          onChange={(value) => actions.style({ 'border-radius': value === 0 ? null : `${value}px` }, false)}
        />
        <Slider
          label="진하기"
          value={Math.round((px(style.opacity) ?? 1) * 100)}
          min={10}
          max={100}
          suffix="%"
          onChange={(value) => actions.style({ opacity: value >= 100 ? null : String(value / 100) }, false)}
        />
      </Section>

      <Section title="자리와 순서">
        <div className="flex gap-1.5">
          <PanelButton
            icon={<AlignCenterVertical className="h-4 w-4" />}
            label="가로 가운데"
            title="감싼 상자(없으면 화면)의 가로 가운데로"
            onClick={() => actions.align('center')}
          />
          <PanelButton
            icon={<AlignCenterHorizontal className="h-4 w-4" />}
            label="세로 가운데"
            title="감싼 상자(없으면 화면)의 세로 가운데로"
            onClick={() => actions.align('middle')}
          />
        </div>
        <div className="flex gap-1.5">
          <PanelButton
            icon={<BringToFront className="h-4 w-4" />}
            label="앞으로"
            title="겹친 것 가운데 한 칸 앞으로"
            onClick={() => actions.reorder(1)}
          />
          <PanelButton
            icon={<SendToBack className="h-4 w-4" />}
            label="뒤로"
            title="겹친 것 가운데 한 칸 뒤로"
            onClick={() => actions.reorder(-1)}
          />
        </div>
        <NumbersFold box={box} multiple={multiple} actions={actions} />
      </Section>

      <div className="space-y-1.5 px-4 py-3.5">
        <div className="flex gap-1.5">
          <PanelButton icon={<Copy className="h-4 w-4" />} label="복제" onClick={actions.duplicate} />
          <PanelButton tone="danger" icon={<Trash2 className="h-4 w-4" />} label="지우기" onClick={actions.remove} />
        </div>
        <div className="flex">
          <PanelButton
            icon={<RotateCcw className="h-4 w-4" />}
            label="이것만 원래대로"
            title="고른 것을 처음 모습으로"
            disabled={!canReset}
            onClick={actions.reset}
          />
        </div>
      </div>
    </div>
  );
}

function TextControls({
  element,
  getView,
  fonts,
  docColors,
  actions,
}: {
  element: HTMLElement;
  getView: () => Window | null;
  fonts: readonly string[];
  docColors: readonly string[];
  actions: EditActions;
}): ReactNode {
  // 계산된 스타일은 props 로 넘기지 않고 여기서 읽는다 — 그 객체는 그릴 때마다 바뀌어 React 개발판이 수백 칸을 훑는다.
  const style = getView()!.getComputedStyle(element);
  const size = Math.round(px(style.fontSize) ?? 16);
  const bold = (px(style.fontWeight) ?? 400) >= 600;
  const align =
    style.textAlign === 'center'
      ? 'center'
      : style.textAlign === 'right' || style.textAlign === 'end'
        ? 'right'
        : 'left';
  const family = style.fontFamily
    .split(',')[0]!
    .trim()
    .replace(/^["']|["']$/g, '');
  const familyOptions = [
    { value: '', label: '이 화면의 글꼴 그대로' },
    ...fonts.map((name) => ({ value: name, label: name })),
    ...GENERIC_FONTS,
  ];
  return (
    <>
      <div>
        <div className="mb-1 text-[12.5px] text-slate-700">글자 크기</div>
        <Stepper
          label="글자 크기"
          value={size}
          suffix="px"
          step={stepFontSize}
          onChange={(value) => actions.style({ 'font-size': `${value}px` }, true)}
        />
      </div>
      <div className="flex gap-1.5">
        <Toggle
          on={bold}
          icon={<Bold className="h-4 w-4" />}
          label="굵게"
          onChange={(on) => actions.style({ 'font-weight': on ? '700' : '400' }, true)}
        />
        <div className="min-w-0 flex-1">
          <Choice
            label="글 정렬"
            value={align}
            onChange={(value) => actions.style({ 'text-align': value }, true)}
            options={[
              { value: 'left', label: '', title: '왼쪽 정렬', icon: <AlignLeft className="h-4 w-4" /> },
              { value: 'center', label: '', title: '가운데 정렬', icon: <AlignCenter className="h-4 w-4" /> },
              { value: 'right', label: '', title: '오른쪽 정렬', icon: <AlignRight className="h-4 w-4" /> },
            ]}
          />
        </div>
      </div>
      <div>
        <div className="mb-1 text-[12.5px] text-slate-700">글자 색</div>
        <ColorChoices
          label="글자 색"
          current={parseCssColor(style.color)}
          docColors={docColors}
          onPick={(css, final) => actions.style({ color: css }, final)}
        />
      </div>
      <div>
        <div className="mb-1 text-[12.5px] text-slate-700">글꼴</div>
        <SelectBox
          label="글꼴"
          value={familyOptions.some((option) => option.value === family) ? family : ''}
          options={familyOptions}
          onChange={(value) =>
            actions.style({ 'font-family': value ? (value.includes(' ') ? `"${value}"` : value) : null }, true)
          }
        />
      </div>
    </>
  );
}

/** 그림 영역 — 내 컴퓨터에서 그림을 골라 넣고, 꽉 채울지 다 보일지 고른다. */
function FrameSection({ frame, actions }: { frame: HTMLElement; actions: EditActions }): ReactNode {
  const filled = !!frameImage(frame);
  return (
    <Section title="그림">
      <div className="flex">
        <PanelButton
          tone={filled ? 'plain' : 'primary'}
          icon={<ImageUp className="h-4 w-4" />}
          label={filled ? '다른 그림으로 바꾸기' : '내 컴퓨터에서 그림 고르기'}
          onClick={actions.frameImage}
        />
      </div>
      <p className="text-[12px] leading-relaxed text-slate-500">
        PNG·JPEG·GIF·WebP·SVG. 큰 사진은 넣기 전에 알맞게 줄입니다. 그림은 이 화면 안에 함께 저장됩니다.
      </p>
      {filled ? (
        <>
          <div>
            <div className="mb-1 text-[12.5px] text-slate-700">맞추기</div>
            <Choice<ImageFit>
              label="그림 맞추기"
              value={frameFit(frame)}
              onChange={actions.frameFit}
              options={[
                { value: 'cover', label: '가득 채우기', title: '영역을 꽉 채웁니다(넘치는 곳은 잘림)' },
                { value: 'contain', label: '전체 보이기', title: '그림 전체가 보입니다(남는 곳은 바탕 색)' },
              ]}
            />
          </div>
          <div className="flex">
            <PanelButton icon={<ImageMinus className="h-4 w-4" />} label="그림 빼기" onClick={actions.frameClear} />
          </div>
        </>
      ) : null}
    </Section>
  );
}

/** 영상 — 콘텐츠로 올린 영상을 가리킨다(`components.ts`). 화면은 소리 없이 되풀이해 튼다(소리는 켤 수 있다). */
function VideoSection({ video, actions }: { video: HTMLElement; actions: EditActions }): ReactNode {
  return (
    <Section title="영상">
      <div className="flex">
        <PanelButton icon={<Clapperboard className="h-4 w-4" />} label="다른 영상으로 바꾸기" onClick={actions.replaceVideo} />
      </div>
      <p className="text-[12px] leading-relaxed text-slate-500">
        영상은 콘텐츠로도 올라가고(화면이 푸는 모양으로 바꿉니다), 화면은 이 HTML 과 함께 받아 둡니다. 에이전트가 새 판이어야
        나옵니다 — 옛 판에서는 이 자리가 검게 남습니다.
      </p>
      <div>
        <div className="mb-1 text-[12.5px] text-slate-700">맞추기</div>
        <Choice<ImageFit>
          label="영상 맞추기"
          value={videoFit(video)}
          onChange={actions.videoFit}
          options={[
            { value: 'cover', label: '가득 채우기', title: '자리를 꽉 채웁니다(넘치는 곳은 잘림)' },
            { value: 'contain', label: '전체 보이기', title: '영상 전체가 보입니다(남는 곳은 검게)' },
          ]}
        />
      </div>
      <Toggle on={videoSound(video)} label={videoSound(video) ? '소리 냄' : '소리 없이'} onChange={actions.videoSound} />
    </Section>
  );
}

/** 입력 칸의 값 — 화면이 처음 뜰 때 이 값이 들어간다(`components.ts`). */
function FieldSection({
  element,
  kind,
  actions,
}: {
  element: HTMLElement;
  kind: NonNullable<ReturnType<typeof fieldKind>>;
  actions: EditActions;
}): ReactNode {
  const value = fieldValue(element);
  const type = (element.getAttribute('type') ?? 'text').toLowerCase();
  const options =
    kind === 'select'
      ? Array.from((element as HTMLSelectElement).options).map((option) => ({
          value: option.value,
          label: option.textContent ?? option.value,
        }))
      : [];
  return (
    <Section title="값">
      {kind === 'check' ? (
        <Toggle
          on={value === 'true'}
          label={value === 'true' ? '체크됨' : '체크 안 됨'}
          onChange={(on) => actions.fieldValue(String(on))}
        />
      ) : kind === 'select' ? (
        <>
          <SelectBox label="고른 항목" value={value} options={options} onChange={actions.fieldValue} />
          <div>
            <div className="mb-1 text-[12.5px] text-slate-700">항목 (한 줄에 하나)</div>
            <LinesField
              label="선택 상자 항목"
              value={options.map((option) => option.label).join('\n')}
              onChange={(text) =>
                actions.selectOptions(
                  text
                    .split('\n')
                    .map((line) => line.trim())
                    .filter(Boolean),
                )
              }
            />
          </div>
        </>
      ) : kind === 'textarea' ? (
        <LinesField label="값" value={value} onChange={actions.fieldValue} />
      ) : (
        <ValueField label="값" type={kind === 'range' ? 'number' : type} value={value} onChange={actions.fieldValue} />
      )}
      <p className="text-[12px] leading-relaxed text-slate-500">
        화면이 뜰 때 이 값이 들어가고, 이 값으로 계산하는 화면이면 그대로 계산됩니다. 편집 화면에서는 계산된 숫자가 비어
        보일 수 있습니다.
      </p>
      {kind === 'text' || kind === 'textarea' ? (
        <div>
          <div className="mb-1 text-[12.5px] text-slate-700">비었을 때 보이는 안내 글</div>
          <ValueField
            label="안내 글"
            type="text"
            value={element.getAttribute('placeholder') ?? ''}
            onChange={actions.placeholder}
          />
        </div>
      ) : null}
    </Section>
  );
}

/** D-day — 기준일을 고르거나 화면의 날짜 칸에 잇고, 남은 날·지난 날과 앞뒤 글을 정한다. 고치면 글이 바로 바뀐다. */
function DdaySection({ element, actions }: { element: HTMLElement; actions: EditActions }): ReactNode {
  const settings = readDday(element);
  const fields = linkableDateFields(element.ownerDocument);
  const linked = settings.from ? fields.find((field) => field.id === settings.from) : undefined;
  const source = settings.from ? 'field' : 'date';
  const today = new Date();
  return (
    <Section title="기준일과 셈">
      <div>
        <div className="mb-1 text-[12.5px] text-slate-700">기준일</div>
        <Choice<'date' | 'field'>
          label="기준일 정하는 법"
          value={source}
          onChange={(value) => {
            if (value === 'date') actions.ddayLink(null);
            else if (fields[0]) actions.ddayLink(fields[0].element);
          }}
          options={[
            { value: 'date', label: '날짜 고르기' },
            ...(fields.length > 0
              ? [{ value: 'field' as const, label: '화면의 날짜 칸', title: '이 화면의 날짜 입력 칸 값을 기준일로' }]
              : []),
          ]}
        />
      </div>
      {source === 'date' ? (
        <ValueField label="기준일" type="date" value={settings.date} onChange={(date) => actions.dday({ date })} />
      ) : (
        <>
          <SelectBox
            label="연결한 날짜 칸"
            value={settings.from ?? ''}
            options={[
              ...(linked ? [] : [{ value: settings.from ?? '', label: '(찾을 수 없는 칸)' }]),
              ...fields.map((field, index) => ({
                value: field.id || `#${index}`,
                label: fieldLabel(field.element) || `날짜 칸 ${index + 1}`,
              })),
            ]}
            onChange={(value) => {
              const found = fields.find((field, index) => (field.id || `#${index}`) === value);
              if (found) actions.ddayLink(found.element);
            }}
          />
          <p className="text-[12px] leading-relaxed text-slate-500">
            그 칸의 값 {ddayDate(element) || '(비어 있음)'} — 칸을 눌러 값을 바꾸면 D-day 도 바로 바뀝니다.
          </p>
        </>
      )}
      <div>
        <div className="mb-1 text-[12.5px] text-slate-700">셈</div>
        <Choice<DdayMode>
          label="셈"
          value={settings.mode}
          onChange={(mode) =>
            actions.dday({
              mode,
              after:
                mode === 'since' && !settings.after
                  ? '일째'
                  : mode === 'until' && settings.after === '일째'
                    ? ''
                    : settings.after,
            })
          }
          options={[
            { value: 'until', label: '남은 날', title: '기준일까지 D-12 · 그날 D-DAY · 지나면 D+3' },
            { value: 'since', label: '지난 날', title: '기준일을 1일째로 세어 오늘이 며칠째' },
          ]}
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="mb-1 text-[12.5px] text-slate-700">앞 글</div>
          <ValueField
            label="앞 글"
            type="text"
            value={settings.before}
            onChange={(before) => actions.dday({ before })}
          />
        </div>
        <div>
          <div className="mb-1 text-[12.5px] text-slate-700">뒤 글</div>
          <ValueField label="뒤 글" type="text" value={settings.after} onChange={(after) => actions.dday({ after })} />
        </div>
      </div>
      <p className="text-[12px] leading-relaxed text-slate-500">
        오늘 기준 「{ddayText(settings, ddayDate(element), today)}」. 화면에서는 켤 때와 자정마다 다시 셉니다.
      </p>
    </Section>
  );
}

/** 숫자로 자리·크기 맞추기 — 잘 쓰지 않으니 접어 둔다. */
function NumbersFold({ box, multiple, actions }: { box: Box; multiple: boolean; actions: EditActions }): ReactNode {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        className="flex w-full items-center gap-1 py-1 text-[12.5px] font-medium text-slate-500 hover:text-slate-800"
        onClick={() => setOpen((value) => !value)}
      >
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? '' : '-rotate-90'}`} />
        숫자로 자리·크기 맞추기
      </button>
      {open ? (
        <div className="mt-1.5 grid grid-cols-2 gap-2">
          <NumberField
            label="왼쪽에서 (px)"
            value={Math.round(box.x)}
            onChange={(value) => actions.box({ x: value }, true)}
          />
          <NumberField
            label="위에서 (px)"
            value={Math.round(box.y)}
            onChange={(value) => actions.box({ y: value }, true)}
          />
          {multiple ? null : (
            <>
              <NumberField
                label="너비 (px)"
                min={1}
                value={Math.round(box.width)}
                onChange={(value) => actions.box({ width: value }, true)}
              />
              <NumberField
                label="높이 (px)"
                min={1}
                value={Math.round(box.height)}
                onChange={(value) => actions.box({ height: value }, true)}
              />
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** 아무것도 고르지 않았을 때 — 쓰는 법과 화면 전체(크기·배경). */
function PagePanel({ getView, body, design, sizeChoices, canResetAll, docColors, actions }: Props): ReactNode {
  const view = getView();
  const color = body && view ? parseCssColor(view.getComputedStyle(body).backgroundColor) : null;
  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="border-b border-slate-100 px-4 py-4">
        <div className="text-[15px] font-semibold text-slate-900">고칠 곳을 화면에서 누르세요</div>
        <ul className="mt-3 space-y-2.5 text-[13px] leading-snug text-slate-600">
          <li className="flex gap-2.5">
            <MousePointerClick className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <span>
              <b className="font-semibold text-slate-800">누르면 고릅니다.</b> 글·그림·상자를 누르면 파란 테두리가
              생기고, 이 칸에서 색과 크기를 바꿉니다.
            </span>
          </li>
          <li className="flex gap-2.5">
            <Move className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <span>
              <b className="font-semibold text-slate-800">끌면 옮겨집니다.</b> 테두리의 동그라미를 끌면 크기가 바뀝니다.
            </span>
          </li>
          <li className="flex gap-2.5">
            <Pencil className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <span>
              <b className="font-semibold text-slate-800">글은 한 번 더 누르면 고칩니다.</b> 다 쓰면 바깥을 누르세요.
            </span>
          </li>
          <li className="flex gap-2.5">
            <Undo2 className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
            <span>
              잘못 고쳤으면 위의 <b className="font-semibold text-slate-800">되돌리기</b>를 누르세요.
            </span>
          </li>
        </ul>
      </div>
      <Section title="화면 크기">
        {sizeChoices && sizeChoices.length > 1 ? (
          <>
            <SelectBox
              label="화면 크기"
              value={`${design.width}x${design.height}`}
              options={sizeChoices.map((choice) => ({
                value: `${choice.width}x${choice.height}`,
                label: `${choice.label} · ${choice.width}×${choice.height}`,
              }))}
              onChange={(value) => {
                const [width, height] = value.split('x').map(Number);
                actions.design({ width: width!, height: height! });
              }}
            />
            <p className="text-[12px] leading-relaxed text-slate-500">
              이 크기에서 자리를 정하고, 화면마다 이 크기를 통째로 맞춰 띄웁니다.
            </p>
          </>
        ) : (
          <p className="text-[13px] text-slate-700">
            {design.width} × {design.height}
            <span className="ml-1.5 text-[12px] text-slate-500">— 화면마다 이 크기를 통째로 맞춰 띄웁니다</span>
          </p>
        )}
      </Section>
      <Section title="화면 배경 색">
        <ColorChoices
          label="화면 배경 색"
          current={color && color.a > 0 ? color : null}
          docColors={docColors}
          none="원래 배경"
          onPick={actions.pageBackground}
        />
      </Section>
      <div className="flex px-4 py-3.5">
        <PanelButton
          icon={<RotateCcw className="h-4 w-4" />}
          label="처음 모습으로 모두 되돌리기"
          disabled={!canResetAll}
          onClick={actions.resetAll}
        />
      </div>
    </div>
  );
}
